import React, { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal } from "@/components/Motion";
import CtaBanner from "@/components/CtaBanner";
import TechMarquee from "@/components/TechMarquee";
import Seo from "@/components/Seo";
import { SERVICES, IMAGES } from "@/lib/site-data";

export default function Services() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace("#", ""));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      }
    }
  }, [location]);

  return (
    <div data-testid="page-services">
      <Seo
        title="Services | IT Consulting, Training, Managed Services, Staffing — Houston TX & Washington DC"
        description="Four practices in one partner: IT Consulting (AI, Cloud, DevOps, Cybersecurity, PM), Training & Certification, Managed IT Services, and Tech Staffing. Serving Houston TX, Richmond, Sugar Land, Fulshear, and Washington DC."
        path="/services"
      />
      {/* Hero */}
      <section className="relative bg-slate-950 text-white pt-36 pb-24 md:pt-44 md:pb-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.servicesTech} alt="Abstract technology network" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/90 to-slate-950" />
          <div className="absolute inset-0 grid-lines-dark opacity-60" />
        </div>
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Reveal><div className="eyebrow text-emerald-400">Services</div></Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.02] max-w-5xl">
              Four practices. One engineering-grade partner.
            </h1>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-8 max-w-3xl text-lg md:text-xl text-slate-300 leading-relaxed">
              Choose one. Combine several. Our clients get better outcomes when consulting, training,
              managed services, and staffing compound together.
            </p>
          </Reveal>

          <div className="mt-12 flex flex-wrap gap-3">
            {SERVICES.map((s) => (
              <a key={s.slug} href={`#${s.slug}`} data-testid={`services-jump-${s.slug}`} className="font-mono text-xs tracking-widest px-4 py-2 border border-slate-700 hover:border-emerald-500 hover:text-emerald-400 transition-colors uppercase">
                {s.code} · {s.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      <TechMarquee theme="dark" />

      {/* Service sections */}
      {SERVICES.map((service, i) => {
        const Icon = service.icon;
        const reverse = i % 2 === 1;
        const bg = i % 2 === 0 ? "bg-white" : "bg-slate-50";
        return (
          <section key={service.slug} id={service.slug} className={`py-28 md:py-36 ${bg} scroll-mt-24`}>
            <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
              <div className={`grid grid-cols-1 lg:grid-cols-12 gap-14 items-start ${reverse ? "lg:[&>*:first-child]:col-start-6" : ""}`}>
                <div className="lg:col-span-7">
                  <Reveal>
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 bg-slate-900 text-emerald-400 flex items-center justify-center">
                        <Icon className="w-5 h-5" strokeWidth={1.5} />
                      </div>
                      <div className="font-mono text-xs tracking-[0.3em] text-slate-500">{service.code} / SERVICE</div>
                    </div>
                  </Reveal>
                  <Reveal delay={0.08}>
                    <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-slate-900">
                      {service.title}
                    </h2>
                    <div className="eyebrow mt-3 text-slate-500">{service.tag}</div>
                  </Reveal>
                  <Reveal delay={0.15}>
                    <p className="mt-6 text-slate-600 text-lg leading-relaxed max-w-2xl">{service.description}</p>
                  </Reveal>

                  <Reveal delay={0.22}>
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {service.benefits.map((b) => (
                        <div key={b} className="flex items-start gap-3 py-2">
                          <Check className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" strokeWidth={1.75} />
                          <span className="text-slate-700">{b}</span>
                        </div>
                      ))}
                    </div>
                  </Reveal>

                  <Reveal delay={0.28}>
                    <div className="mt-10">
                      <Link
                        to="/contact"
                        data-testid={`service-cta-${service.slug}`}
                        className="group inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-4 font-medium hover:bg-emerald-500 hover:text-slate-950 transition-colors"
                      >
                        Request Service
                        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.75} />
                      </Link>
                    </div>
                  </Reveal>
                </div>

                <div className="lg:col-span-5">
                  <Reveal delay={0.1}>
                    <div className="relative">
                      <div className="absolute inset-0 border border-emerald-500 translate-x-4 translate-y-4" />
                      <div className="relative bg-slate-950 text-white p-10 min-h-[420px] flex flex-col justify-between">
                        <div className="font-mono text-[11px] text-emerald-400 tracking-[0.3em] uppercase">Engagement</div>
                        <div>
                          <div className="font-display text-3xl md:text-4xl font-semibold leading-tight">
                            {service.slug === "consulting" && "Strategy → delivery. One accountable team."}
                            {service.slug === "training" && "Cohort-based. Instructor-led. Certification-ready."}
                            {service.slug === "managed" && "24/7 run and optimize. Zero surprises."}
                            {service.slug === "staffing" && "Vetted talent. Placed in under two weeks."}
                          </div>
                          <div className="mt-6 border-t border-slate-800 pt-6 space-y-2 text-sm text-slate-300">
                            {service.slug === "consulting" && (
                              <>
                                <div>· AI readiness & data platform strategy</div>
                                <div>· Cloud migration, modernization, FinOps</div>
                                <div>· DevOps / Platform / SRE advisory</div>
                                <div>· Technical due diligence</div>
                              </>
                            )}
                            {service.slug === "training" && (
                              <>
                                <div>· Artificial Intelligence (AI) & Machine Learning</div>
                                <div>· AWS / Azure / GCP certification tracks</div>
                                <div>· DevOps, Kubernetes, Cyber bootcamps</div>
                                <div>· Data engineering & AI/ML programs</div>
                                <div>· Project Management</div>
                                <div>· Private enterprise cohorts</div>
                              </>
                            )}
                            {service.slug === "managed" && (
                              <>
                                <div>· Infrastructure monitoring & incident response</div>
                                <div>· Security, patching, compliance</div>
                                <div>· Backup, DR, business continuity</div>
                                <div>· Cost optimization & FinOps</div>
                              </>
                            )}
                            {service.slug === "staffing" && (
                              <>
                                <div>· Senior engineers, architects, leaders</div>
                                <div>· Contract, C2H, direct placement</div>
                                <div>· Global + US-based talent</div>
                                <div>· Typical ramp: 7–14 days</div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <CtaBanner
        title="Ready to scope your engagement?"
        body="Share your goals in a 30-minute call. We'll respond with a proposal, references, and a plan — inside 48 hours."
      />
    </div>
  );
}
